import { AI } from "@/lib/ai/ai"
import { getOrExtractImageInfo, getImageInfo, updateImageInfo } from "@/store/processing/image-info"
import { storeAdjustmentsHistoryAndUpdatePhoto } from "@/store/processing/processing-storage"
import {
	adaptExposureOnlyFromKeyPhoto,
	applyReverseStyleWBBiases,
	applyStyleBiases,
	applyWeightedStyleBiases,
	calculateAIProcessingData,
	combineFinalAdjustments,
} from "@/lib/ai/ai-style"
import {
	currentPresetAdjustmentsAtom,
	getPresetIdWithStrength,
	loadingPresetsAtom,
	loadPresetAdjustments,
	nullPresetID,
	PresetAdjustmentsInfo,
	PresetAdjustmentsResult,
} from "@/store/store-presets"
import { AbortError, isAbortError } from "@/utils/error"
import { beginAIStylePhoto } from "./ai-style-performance"
import { processingQueue } from "@/store/processing/processing-queue"
import { approveReferenceTask } from "@/store/processing/mark-reference"
import { CatalogManager, ImageInfoRecord } from "@/lib/catalog"
import {
	getPhotoMetadata,
	photosAtom,
	sortedPhotosAtom,
	updatePhoto,
	updatePhotos,
} from "@/store/photo/photos-state"
import { HistoryRecordAction, PhotoRecord, StyleInfoRecord } from "@/lib/catalog/types"
import { getFileForPhoto } from "../files-state"
import { store } from "@/store/store"
import { applyAIAdjustmentsAtom } from "@/store/settings-state"
import { Adjustments } from "@/core/types"
import {
	DefaultExcludedAdjustmentKeys,
	applyPresetStrengthToUserLutAdjustments,
	defaultWhiteBalance,
	getDefaultAdjustments,
	interpolateAdjustments,
	interpolateFullAdjustments,
} from "@/core/adjustments"
import {
	currentStyleAtom,
	isLocalStyle,
	referenceImagesAtomFamily,
	Style,
} from "../style/style-state"
import { adjustmentsContainsNaN, logAiInfo } from "@/lib/ai/utils"
import {
	addAutoCorrectionParamsIfNeeded,
	aggregatePresetsAndCheckOverwriting,
	checkAndRemoveNonExistingMasks,
	combineAdjustmentsAndMaintainNonColorParams,
	getAdvancedParams,
	getBaseExposure,
	getBaseWB,
	getFeatureDistAndColorStrength,
	getInputColorMatchFeatureWithFaceData,
	includesAIMask,
	limitLightingRanges,
	saveInputColorMatchFeatureToImageInfo,
	transformLocalAdjustmentsForPaste,
	transformPresetMaskForPaste,
} from "./processing-utils"
import { cached } from "@/utils/cached"
import { PresetItem } from "@/store/presets/presets-service"
import { Deferred } from "@/utils/deferred"
import { isManualModeAtom, projectAtom } from "../project/projects-state"
import {
	beginTaskSingleItemPerformanceMeasurement,
	commitTaskSingleItemPerformanceMeasurement,
} from "../debug/task-performance-state"
import { applyColorMatchAdjustments } from "../colorMatch/color-match-processing"
import { ColorMatchImageItem } from "../colorMatch/color-match-service"
import { debounce } from "@/utils/debounce"
import {
	ColorMatchRuntimeInfo,
	InputColorMatchFeaturesWithFaceData,
	ReferenceColorMatchFeatures,
	ReferenceItem,
} from "@/lib/ai/color-match/utils"
import {
	getValidNearestRef,
	getNumRefsForColorMatch,
	imageWbDistance,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { getEditor } from "@/utils/useEditor"
import { WhiteBalance } from "@/core/photo"
import { getAutoLightParams } from "@/lib/ai/calibration/ai-calibration-brightness"
import { enableHighlightCorrection } from "@/lib/ai/color-match/parameters"
import { transfer } from "comlink"
import { getReferenceCacheId } from "@/lib/ai/color-match/utils"
import { colorMatchCache } from "../colorMatch/color-match-cache"
import { AI_WORKER_REF_CACHE_MISS_ERROR } from "@/lib/ai/utils"
import { Sentry } from "@/telemetry/sentry"
import { isEmpty } from "@/lib/ai/calibration/ai-calibration-utils"
import { fetchPreviewWithTags } from "@/storage/indexeddb"
import { PreviewSize } from "../previews/previews-types"
import { hasInvalidAdjustments, sanitizeAdjustments } from "@/core/utils/adjustments-validation"
import {
	clearColorMatchRuntimeInfo,
	setColorMatchRuntimeInfo,
} from "../debug/color-match-runtime-state"
import {
	getReferenceWebpData256x256,
	hasReferenceWebpData256x256Source,
} from "@/lib/style/reference-webp-data"

type WaitBlockId = "adjustments"

const copyUint8Array = (data: Uint8Array): Uint8Array => new Uint8Array(data)

export interface AdjustmentsResult {
	adjustments: Adjustments
	preview: Blob | null | Promise<Blob> | Promise<null>
	imageInfo: ImageInfoRecord | null
	originalPreview: Blob | null | Promise<Blob> | Promise<null>
}

interface ApplyPresetResult {
	finalResults: AdjustmentsResult | null
	presetResults: AdjustmentsResult
}

const waitBlocks = new Map<WaitBlockId, Deferred<void>>()
const AI_STYLE_EDITOR_CLEANUP_INTERVAL = 5
let completedAIStyleApplicationsSinceEditorCleanup = 0

export function resumeTasksOrClearStatus(triggerSource?: string | null) {
	const photosToClearStatus: PhotoRecord[] = []
	for (const photo of store.get(sortedPhotosAtom)) {
		if (photo.status === "applying" || photo.status === "pending") {
			if (!photo.currentTask) {
				photosToClearStatus.push(photo)
			} else {
				switch (photo.currentTask.type) {
					case "apply-preset":
						queueApplyPreset(
							photo,
							photo.currentTask.preset,
							photo.currentTask.strength,
							photo.currentTask.allowStrengthAdjustment,
						)
						break
					case "apply-style": {
						const currentStyle = store.get(currentStyleAtom)
						if (currentStyle && currentStyle.styleId === photo.currentTask.styleId) {
							queueApplyStyle(photo, currentStyle, triggerSource)
						} else {
							photosToClearStatus.push(photo)
						}
						break
					}
					case "apply-auto":
						queueApplyAutoAdjustments(photo)
						break
					case "mark-reference":
						queueMarkReference(photo)
						break
					case "apply-color-match":
						queueApplyColorMatch({
							photo,
							referenceImage: photo.currentTask.referenceImage,
							useParameters: photo.currentTask.useParameters,
							strength: photo.currentTask.strength,
							skinToneOptimized: photo.currentTask.skinToneOptimized,
							asShotWhiteBalance: photo.currentTask.asShotWhiteBalance,
						})
						break
				}
			}
		}
	}

	if (photosToClearStatus.length > 0) {
		updatePhotos((photos) => {
			for (const photo of photosToClearStatus) {
				photos[photo.id] = { status: "none" }
			}
		})
	}
}

export function queueWaitBlock(id: WaitBlockId) {
	const deferred = new Deferred<void>()

	if (waitBlocks.get(id)) {
		return
	}

	waitBlocks.set(id, deferred)

	processingQueue.addTask(id, {
		purpose: "wait",
		handler: async () => {
			await deferred.promise
		},
	})

	return deferred
}

export function resolveWaitBlock(id: WaitBlockId) {
	waitBlocks.get(id)?.resolve()
	waitBlocks.delete(id)
}

export function hasWaitBlock(id: WaitBlockId) {
	return waitBlocks.has(id)
}

export function queueMarkReference(photo: PhotoRecord, completionHandler?: () => void) {
	processingQueue.addTask(photo.id, {
		purpose: "calculate",
		handler: () => approveReferenceTask(photo, completionHandler),
		taskRecord: { type: "mark-reference" },
	})
}

export function queueApplyStyle(
	photo: PhotoRecord,
	style: StyleInfoRecord,
	triggerSource?: string | null,
) {
	if (!shouldApplyStyle(photo)) {
		return
	}

	processingQueue.addTask(photo.id, {
		purpose: "apply",
		handler: () => applyStyleAndStoreResults(photo, style, triggerSource),
		taskRecord: { type: "apply-style", styleId: style.styleId },
	})
}

export function queueApplyPreset(
	photo: PhotoRecord,
	preset: PresetItem,
	strength = 1,
	allowStrengthAdjustment = false,
	onCompletion?: () => void,
) {
	if (!shouldApplyPreset(photo, preset, strength)) {
		onCompletion?.()
		return
	}

	const presetWithoutAdjustments = { ...preset, adjustments: undefined }
	processingQueue.addTask(photo.id, {
		purpose: "apply",
		handler: () =>
			applyPresetTask(photo, preset, strength, allowStrengthAdjustment, onCompletion),
		taskRecord: {
			type: "apply-preset",
			preset: presetWithoutAdjustments,
			strength,
			allowStrengthAdjustment,
		},
	})
}

export const queuePreviewPresetDebounced = debounce(queuePreviewPreset, 20)

export function queuePreviewPreset(
	photo: PhotoRecord,
	preset: PresetItem,
	strength = 1,
	resultHandler: (result: { presetResult: PresetAdjustmentsResult; photoId: string }) => void,
) {
	processingQueue.addTask(photo.id, {
		purpose: "calculate",
		handler: () => previewPresetTask(photo, preset, strength, resultHandler),
	})
}

export function queueApplyAutoAdjustments(photo: PhotoRecord) {
	if (photo.isUnsupported || photo.isHidden) {
		return
	}

	if (processingQueue.hasTask(photo.id, (task) => task.taskRecord?.type === "apply-auto")) {
		return
	}

	processingQueue.addTask(photo.id, {
		purpose: "apply",
		handler: () => applyAutoAdjustmentsTask(photo),
		taskRecord: { type: "apply-auto" },
	})
}

export function queueApplyBasicAutoAdjustments(photo: PhotoRecord) {
	if (photo.isHidden) {
		return
	}
	processingQueue.addTask(photo.id, {
		purpose: "apply",
		handler: () => applyBasicAutoAdjustmentsTask(photo),
		taskRecord: { type: "apply-basic-auto" },
	})
}

export function queuePasteAdjustments(photo: PhotoRecord, adjustments: Partial<Adjustments>) {
	if (photo.isUnsupported || photo.isHidden) {
		return
	}
	processingQueue.addTask(photo.id, {
		purpose: "apply",
		handler: () => pasteAdjustmentsTask(photo, adjustments),
	})
}

export function queueApplyColorMatch(props: {
	photo: PhotoRecord
	referenceImage: ColorMatchImageItem
	useParameters: boolean
	strength: number
	skinToneOptimized: boolean
	asShotWhiteBalance: WhiteBalance
}) {
	if (props.photo.isUnsupported || props.photo.isHidden) {
		return
	}
	processingQueue.addTask(props.photo.id, {
		purpose: "apply",
		handler: () => applyColorMatchAdjustments({ ...props, generatePreview: true }),
		taskRecord: {
			type: "apply-color-match",
			referenceImage: props.referenceImage,
			useParameters: props.useParameters,
			strength: props.strength,
			skinToneOptimized: props.skinToneOptimized,
			asShotWhiteBalance: props.asShotWhiteBalance,
		},
	})
}

export async function applyStyleAndStoreResults(
	photo: PhotoRecord,
	style: StyleInfoRecord,
	triggerSource?: string | null,
) {
	const project = store.get(projectAtom)
	if (!project) {
		throw new Error("Project closed, skipping style application")
	}

	const latestPhoto = store.get(photosAtom)[photo.id]
	if (!latestPhoto || latestPhoto.isHidden) {
		throw new AbortError("Photo is hidden")
	}
	if (!shouldApplyStyle(latestPhoto)) {
		throw new AbortError("Style is already applied")
	}

	const measurementTask = beginTaskSingleItemPerformanceMeasurement("ai-style-apply")
	const performance = beginAIStylePhoto(project.id, style.styleId, triggerSource)
	try {
		await AI.lockEditor()

		const ignoreClustering = style.prioritizeImageIds?.includes(photo.id) ?? false
		const existingRenderedPreview = await fetchPreviewWithTags({
			photoId: photo.id,
			size: PreviewSize.medium,
			projectId: project.id,
			tags: ["rendered", "original"],
		})

		const shouldGenerateOriginalPreview = !existingRenderedPreview
		let results: AdjustmentsResult
		try {
			results = await applyStyle({
				photo: latestPhoto,
				style,
				ignoreClustering,
				shouldGenerateOriginalPreview,
			})
		} finally {
			try {
				const trimResult = await AI.trimTfjsWebgpuFreeResources()
				if (trimResult.destroyFailures > 0) {
					console.warn(
						"[AI Style] Some TFJS WebGPU free resources could not be destroyed",
						trimResult,
					)
				}
			} catch (error) {
				console.warn("[AI Style] Failed to trim TFJS WebGPU free resources", error)
				Sentry.captureException(error, {
					tags: { description: "AI Style TFJS WebGPU free-resource trim failed" },
				})
			}
		}

		commitTaskSingleItemPerformanceMeasurement(measurementTask)

		const updatedPhoto = store.get(photosAtom)[photo.id]
		if (!updatedPhoto || updatedPhoto.isHidden) {
			throw new AbortError("Photo is hidden")
		}

		await storeAdjustmentsHistoryAndUpdatePhoto(updatedPhoto, results, {
			message: "Apply AI Style",
			// store presetId for correct mark-reference behavior after user click history
			presetId: results.imageInfo?.presetId ?? undefined,
			styleId: style.styleId,
			styleVersion: style.version,
			action: HistoryRecordAction.ApplyAIStyle,
			changedValue: style.name,
		})
		completedAIStyleApplicationsSinceEditorCleanup++
		if (completedAIStyleApplicationsSinceEditorCleanup >= AI_STYLE_EDITOR_CLEANUP_INTERVAL) {
			completedAIStyleApplicationsSinceEditorCleanup = 0
			// Preview rendering can outlive the history write. Keep the editor lock and
			// wait for those GPU operations before deleting the renderer's resources.
			await Promise.allSettled([results.preview, results.originalPreview])
			try {
				await AI.cleanupEditor()
			} catch (error) {
				console.warn(
					"[AI Style] Failed to clean up the editor at the batch interval",
					error,
				)
				Sentry.captureException(error, {
					tags: { description: "Periodic AI Style editor cleanup failed" },
				})
			}
		}
		performance.processed(photo.fileName)
	} catch (error) {
		if (!isAbortError(error)) performance.failed()
		throw error
	} finally {
		AI.unlockEditor()
	}
}

function getAdjustmentsForStrength(info: PresetAdjustmentsInfo, strength: number) {
	if (strength === 0) {
		return info.min
	} else if (strength === 1) {
		return info.mid
	} else if (strength === 2) {
		return info.max
	} else if (strength > 0 && strength < 1) {
		const result: PresetAdjustmentsResult = {
			presetId: info.min.presetId,
			strength,
			presetAdjustments: interpolateFullAdjustments(
				info.min.presetAdjustments,
				info.mid.presetAdjustments,
				strength,
			),
			finalAdjustments:
				info.min.finalAdjustments != null && info.mid.finalAdjustments != null
					? interpolateFullAdjustments(
							info.min.finalAdjustments,
							info.mid.finalAdjustments,
							strength,
						)
					: null,
		}
		return result
	} else if (strength > 1 && strength < 2) {
		const result: PresetAdjustmentsResult = {
			presetId: info.min.presetId,
			strength,
			presetAdjustments: interpolateFullAdjustments(
				info.mid.presetAdjustments,
				info.max.presetAdjustments,
				strength - 1,
			),
			finalAdjustments:
				info.mid.finalAdjustments != null && info.max.finalAdjustments != null
					? interpolateFullAdjustments(
							info.mid.finalAdjustments,
							info.max.finalAdjustments,
							strength - 1,
						)
					: null,
		}
		return result
	} else {
		return undefined
	}
}

export function shouldApplyAutoOnPreset() {
	return !store.get(isManualModeAtom) && store.get(applyAIAdjustmentsAtom)
}

function needReComputePresetInfo(
	presetAdjustmentsInfo: PresetAdjustmentsInfo,
	presetId: string,
	photo: PhotoRecord,
) {
	return (
		presetAdjustmentsInfo?.photoId !== photo.id ||
		presetAdjustmentsInfo?.min.presetId !== presetId ||
		presetAdjustmentsInfo?.currentAdjustmentsDigest !== photo.adjustmentsDigest ||
		(shouldApplyAutoOnPreset() &&
			[
				presetAdjustmentsInfo?.min,
				presetAdjustmentsInfo?.mid,
				presetAdjustmentsInfo?.max,
			].some((result) => result?.finalAdjustments == null))
	)
}

export function getCurrentPresetAdjustments(
	photo: PhotoRecord,
	preset: PresetItem,
	strength: number,
) {
	const presetAdjustmentsInfo = store.get(currentPresetAdjustmentsAtom)

	if (
		!presetAdjustmentsInfo ||
		needReComputePresetInfo(presetAdjustmentsInfo, preset.id, photo)
	) {
		return undefined
	}

	const result = getAdjustmentsForStrength(presetAdjustmentsInfo, strength)

	if (result) {
		return result
	}

	return result
}

export async function getOrFetchAdjustments(
	photo: PhotoRecord,
	preset: PresetItem,
	strength: number,
	allowStrengthAdjustment = false,
) {
	let presetAdjustmentsInfo = store.get(currentPresetAdjustmentsAtom)

	if (
		!presetAdjustmentsInfo ||
		needReComputePresetInfo(presetAdjustmentsInfo, preset.id, photo)
	) {
		const { result, presetAdjustments } = await getPresetAdjustments(photo, preset)
		presetAdjustmentsInfo = result

		storePresetToImageInfoIfNeeded(photo, preset, presetAdjustments)

		//store adjustments for preset for interpolation during applyPreset
		if (allowStrengthAdjustment) {
			store.set(currentPresetAdjustmentsAtom, presetAdjustmentsInfo)
		}
	}

	const result = getAdjustmentsForStrength(presetAdjustmentsInfo, strength)

	if (result) {
		return result
	}

	return result
}

async function previewPresetTask(
	photo: PhotoRecord,
	preset: PresetItem,
	strength: number,
	resultHandler: (result: { presetResult: PresetAdjustmentsResult; photoId: string }) => void,
) {
	const presetResult = await getOrFetchAdjustments(photo, preset, strength)
	if (presetResult) {
		resultHandler({ presetResult, photoId: photo.id })
	}
}

async function applyPresetTask(
	photo: PhotoRecord,
	preset: PresetItem,
	strength: number,
	allowStrengthAdjustment: boolean,
	onCompletion?: () => void,
) {
	const latestPhoto = store.get(photosAtom)[photo.id]
	if (!latestPhoto || latestPhoto.isHidden) {
		throw new AbortError("Photo is hidden")
	}

	try {
		await AI.lockEditor()

		if (!shouldApplyPreset(latestPhoto, preset, strength)) {
			throw new AbortError("Preset is already applied")
		}

		// Snapshot once: the toggle can flip while the awaits below run
		const applyAuto = shouldApplyAutoOnPreset()
		const adjustments = await getOrFetchAdjustments(
			latestPhoto,
			preset,
			strength,
			allowStrengthAdjustment,
		)

		if (!adjustments) {
			throw new AbortError("No adjustments found for preset")
		}

		const { finalResults, presetResults } = await applyPreset(
			latestPhoto,
			adjustments,
			applyAuto,
		)
		const presetId = getPresetIdWithStrength(preset.id, strength)
		// store in history: 1st step is pure preset adjustments
		let updatedPhoto = store.get(photosAtom)[photo.id]
		if (!updatedPhoto || updatedPhoto.isHidden) {
			throw new AbortError("Photo is hidden")
		}
		updatedPhoto = await storeAdjustmentsHistoryAndUpdatePhoto(
			updatedPhoto,
			presetResults,
			{
				message: `Apply Preset: ${preset.name}`,
				presetId: presetId,
				action: HistoryRecordAction.ApplyPreset,
				changedValue: `${Math.round(strength * 100)}`,
			},
			false,
		)

		if (updatedPhoto && finalResults) {
			const updatedPhotoAfterPreset = updatedPhoto ?? store.get(photosAtom)[photo.id]
			if (!updatedPhotoAfterPreset || updatedPhotoAfterPreset.isHidden) {
				throw new AbortError("Photo is hidden")
			}
			// 2nd step is auto on top of preset if applicable
			updatedPhoto = await storeAdjustmentsHistoryAndUpdatePhoto(
				updatedPhotoAfterPreset,
				finalResults,
				{
					message: `Auto Lighting & WB`,
					presetId: presetId,
					action: HistoryRecordAction.AutoEnhance, // action is AutoEnhance with presetId != null
				},
				false,
			)
		}

		if (updatedPhoto) {
			//Storing the final photo
			updatePhoto(photo.id, updatedPhoto)
		}
	} finally {
		AI.unlockEditor()
		onCompletion?.()
	}
}

async function storePresetToImageInfoIfNeeded(
	photo: PhotoRecord,
	preset: PresetItem,
	presetAdjustments: Partial<Adjustments>,
) {
	// Store single preset adjustments in imageInfo only when style.presets doesn't have it.
	const style = store.get(currentStyleAtom)
	if (style?.presets[preset.id] == undefined) {
		const imageInfo = await getOrExtractImageInfo(photo)
		if (!imageInfo.presetAdjustmentsMap || !imageInfo.presetAdjustmentsMap[preset.id]) {
			const updatedMap = {
				...(imageInfo.presetAdjustmentsMap ?? {}),
				[preset.id]: presetAdjustments,
			}
			updateImageInfo({ ...imageInfo, presetAdjustmentsMap: updatedMap })
		}
	}
}

export const saveColorProfileHistory = async (options: {
	message: string
	photo: PhotoRecord
	changedValue?: string
}) => {
	const { message, photo, changedValue } = options
	const adjustments = getEditor().adjustments

	const result: AdjustmentsResult = {
		adjustments,
		preview: null,
		originalPreview: null,
		imageInfo: null,
	}

	const updatedPhoto = await storeAdjustmentsHistoryAndUpdatePhoto(
		photo,
		result,
		{
			message: message,
			action: HistoryRecordAction.ManuallyAdjust,
			changedValue,
		},
		false,
	)
	if (updatedPhoto) {
		//Storing the final photo
		updatePhoto(photo.id, updatedPhoto)
	}
}

async function applyBasicAutoAdjustmentsTask(photo: PhotoRecord) {
	const latestPhoto = store.get(photosAtom)[photo.id]
	if (!latestPhoto || latestPhoto.isHidden) {
		throw new AbortError("Photo is hidden")
	}

	const results = await applyBasicAutoAdjustments(latestPhoto)

	const updatedPhoto = store.get(photosAtom)[photo.id]
	if (!updatedPhoto || updatedPhoto.isHidden) {
		throw new AbortError("Photo is hidden")
	}

	storeAdjustmentsHistoryAndUpdatePhoto(updatedPhoto, results, {
		message: `Auto Lighting & WB`,
		action: HistoryRecordAction.AutoEnhance,
	})
}

async function applyAutoAdjustmentsTask(photo: PhotoRecord) {
	const latestPhoto = store.get(photosAtom)[photo.id]
	if (!latestPhoto || latestPhoto.isHidden) {
		throw new AbortError("Photo is hidden")
	}

	try {
		await AI.lockEditor()

		const results = await applyAutoAdjustments(latestPhoto)

		const updatedPhoto = store.get(photosAtom)[photo.id]
		if (!updatedPhoto || updatedPhoto.isHidden) {
			throw new AbortError("Photo is hidden")
		}

		storeAdjustmentsHistoryAndUpdatePhoto(updatedPhoto, results, {
			message: `Auto Lighting & WB`,
			action: HistoryRecordAction.AutoEnhance,
		})
	} finally {
		AI.unlockEditor()
	}
}

async function pasteAdjustmentsTask(photo: PhotoRecord, adjustments: Partial<Adjustments>) {
	const latestPhoto = store.get(photosAtom)[photo.id]
	if (!latestPhoto || latestPhoto.isHidden) {
		throw new AbortError("Photo is hidden")
	}

	try {
		await AI.lockEditor()

		const results = await pasteAdjustments(latestPhoto, adjustments)

		const updatedPhoto = store.get(photosAtom)[photo.id]
		if (!updatedPhoto || updatedPhoto.isHidden) {
			throw new AbortError("Photo is hidden")
		}

		storeAdjustmentsHistoryAndUpdatePhoto(updatedPhoto, results, {
			message: `Paste adjustments`,
			action: HistoryRecordAction.PasteAdjustments,
		})
	} finally {
		AI.unlockEditor()
	}
}

export function shouldApplyStyle(photo: PhotoRecord) {
	if (photo.isUnsupported) {
		return false
	}
	if (photo.isHidden == true) {
		return false
	}
	if (photo.kind == "reference" || photo.kind == "locked") {
		return false
	}

	return true
}

/**
 * Applies an AI style to a single photo. The two algorithm paths converge here:
 *
 * **New algorithm** (neural color match, when `nearestRef.colorMatchReference !== null`):
 *   `getAdjustmentsFromColorMatch` returns estimated params → `finalBias = {}` (bias bypassed).
 *   Final: `combineFinalAdjustments(preset + colorMatchResult, {}, keyAdjustments, imageInfo)`
 *
 * **Old algorithm** (rule-based, when `colorMatchReference === null`):
 *   `getAdjustmentsFromColorMatch` returns `undefined` → falls back to `calculateAutoParams`.
 *   `finalBias = imageInfo.biases` (reference's stored user_edit − auto deltas).
 *   Final: `combineFinalAdjustments(preset + auto, bias, keyAdjustments, imageInfo)`
 *
 * If color match returns NaN (error), gracefully falls back to the old algorithm path.
 *
 * @param ignoreClustering - Skip cluster/key-photo lookup; treat this photo as its own key.
 * @param forceApply - Bypass the "already applied" guard.
 * @param shouldGenerateOriginalPreview - If true, also captures an unedited "before" preview.
 */
export async function applyStyle(props: {
	photo: PhotoRecord
	style: Style
	ignoreClustering?: boolean
	forceApply?: boolean
	shouldGenerateOriginalPreview?: boolean
}): Promise<AdjustmentsResult> {
	const {
		photo,
		style,
		ignoreClustering = false,
		forceApply = false,
		shouldGenerateOriginalPreview = false,
	} = props
	const referenceImages = isLocalStyle(style)
		? store.get(referenceImagesAtomFamily())
		: style.referenceImages

	if (!forceApply && !shouldApplyStyle(photo)) {
		throw new AbortError("Style is already applied")
	}

	const file = getFileForPhoto(photo)

	if (!file) {
		throw new Error("Photo record not found")
	}

	logAiInfo("[applyStyle] for", photo.id, { styleName: style.name, ignoreClustering })

	const currentImageInfo = await getOrExtractImageInfo(photo)

	const metadata = getPhotoMetadata(photo.id)

	await AI.loadImage(file, { metadata })

	let originalPreview: Blob | null | Promise<Blob> | Promise<null> = null

	if (shouldGenerateOriginalPreview) {
		originalPreview = await AI.generatePreview({
			adjustments: getDefaultAdjustments(photo),
		})
	}

	// Determine preset and bias for new or updated style
	const { presetAdjustments, keyAdjustments, bias, imageInfo, keyPhotoColorAdjustments } =
		await calculateAIProcessingData(style, currentImageInfo, ignoreClustering)
	logAiInfo("[applyStyle] processing data for", photo.id, {
		imageInfo,
		presetAdjustments,
		keyAdjustments,
		keyPhotoColorAdjustments,
	})

	if (!bias) {
		// AI.unlockEditor()
		throw new Error("Bias is missing")
	}

	const defaultAdjustments = getDefaultAdjustments(photo)

	let validPresetAdjustments = presetAdjustments
	if (hasInvalidAdjustments(presetAdjustments)) {
		const { sanitizedAdjustments, invalidAdjustments } = sanitizeAdjustments(
			presetAdjustments,
			defaultAdjustments,
		)
		validPresetAdjustments = sanitizedAdjustments
		Sentry.captureException(new Error("Preset adjustments are invalid"), {
			extra: {
				invalidAdjustments: JSON.stringify(invalidAdjustments),
			},
		})
		console.error("Preset adjustments are invalid", {
			presetAdjustments,
			validPresetAdjustments,
			invalidAdjustments,
		})
	}

	const transformedPresetAdjustments = transformPresetMaskForPaste(
		imageInfo,
		validPresetAdjustments,
		defaultAdjustments,
	)
	const baseAdjustments = { ...defaultAdjustments, ...transformedPresetAdjustments }

	let aiAdjustments: Partial<Adjustments> | undefined = undefined

	// Use new color match algorithm to replace auto+bias as long as nearestRef.colorMatchReference is not null
	aiAdjustments = await getAdjustmentsFromColorMatch({
		imageInfo,
		photo,
		keyAdjustments,
		presetAdjustments: imageInfo.presetId ? baseAdjustments : undefined,
		keyPhotoColorAdjustments,
		referenceImages: referenceImages,
	})

	// safe guard
	if (aiAdjustments && adjustmentsContainsNaN(aiAdjustments)) {
		const profileCurve = await AI.getProfileCurve()
		const errorData = {
			aiAdjustments: JSON.stringify(aiAdjustments),
			imageInfo,
			profileCurve,
			presetAdjustments: JSON.stringify(presetAdjustments),
			validPresetAdjustments: JSON.stringify(validPresetAdjustments),
			firstReference: referenceImages[imageInfo.nearestReferences?.[0]?.[0] ?? ""],
			secondReference: referenceImages[imageInfo.nearestReferences?.[1]?.[0] ?? ""],
			thirdReference: referenceImages[imageInfo.nearestReferences?.[2]?.[0] ?? ""],
		}

		console.error("Color Match Adjustments contains NaN", errorData)

		const error = new Error("Color Match Adjustments contains NaN")
		Sentry.captureException(error, {
			extra: errorData,
		})

		// Don't throw error here, just set aiAdjustments to undefined to use old auto adjustments
		aiAdjustments = undefined
	}

	let finalBias = bias
	// old auto adjustments
	if (!aiAdjustments) {
		clearColorMatchRuntimeInfo(photo.id)
		const presetId = imageInfo.presetId ?? nullPresetID
		aiAdjustments = imageInfo.aiAdjustmentsMap[presetId]
		if (!aiAdjustments) {
			// Preset adjustments updated. Need to re-calculate Auto Lighting & WB
			// Also calculate Auto Lighting & WB for the first time if needed.
			aiAdjustments = await AI.calculateAutoParams(baseAdjustments, imageInfo)

			// Add auto perspective or straighten params:
			aiAdjustments = addAutoCorrectionParamsIfNeeded(aiAdjustments, currentImageInfo)

			imageInfo.aiAdjustmentsMap[presetId] = aiAdjustments
		}
		logAiInfo("[applyStyle] apply style using old auto adjustments for", photo.id, {
			aiAdjustments,
			bias,
		})
	} else {
		// If color match is used, no need to apply bias
		finalBias = {}

		logAiInfo("[applyStyle] apply style using color match for", photo.id, { aiAdjustments })
	}

	// Apply style biases that're based on user's choice in style tuning dialog
	aiAdjustments = applyWeightedStyleBiases(
		aiAdjustments,
		imageInfo,
		referenceImages,
		keyAdjustments,
	)

	// Apply final calculated adjustments
	const adjustments = combineFinalAdjustments(
		{ ...baseAdjustments, ...aiAdjustments },
		finalBias,
		keyAdjustments,
		imageInfo,
	)

	if (adjustmentsContainsNaN(adjustments)) {
		const profileCurve = await AI.getProfileCurve()
		const errorData = {
			adjustments,
			imageInfo,
			profileCurve,
		}

		console.error("AI Style Adjustments contains NaN", errorData)

		const error = new Error("AI Style Adjustments contains NaN")
		Sentry.captureException(error, {
			extra: errorData,
		})
		throw error
	}

	let preview: Blob | null | Promise<Blob> | Promise<null> = null
	if (includesAIMask(adjustments)) {
		preview = await AI.generatePreview({ adjustments })
		// Mask detection is finished already in the above step;
		// here we remove the mask from the final adjustments if it does not exist.
		// This function will mutate adjustments and keyAdjustments in place
		await checkAndRemoveNonExistingMasks(adjustments, keyAdjustments)
	} else {
		// not await here to improve performance
		preview = AI.generatePreview({ adjustments })
	}

	// store the final aiStyleAdjustments here
	imageInfo.aiStyleAdjustments = adjustments

	imageInfo.needApplyStyle = false // finished applying style, update status

	logAiInfo("[applyStyle] final adjustments:", photo.id, { ...adjustments })

	return {
		originalPreview,
		preview,
		adjustments,
		imageInfo,
	}
}

/**
 * Main orchestrator for AI style color match. Decides `baseAdjustments` and `computeFullParams`,
 * then delegates to `getColorMatchParamsFromOneRef` or `getColorMatchParamsFromMultipleRefs`.
 *
 * `baseAdjustments` serves two roles:
 *   1. Always used to render `inputWithPreset` for the neural network (improves LUT quality
 *      by showing the model a well-exposed image instead of a dark/clipped one).
 *   2. When `computeFullParams=false`, also used as the preset base for parameter estimation
 *      (only lighting/WB/select HSL are updated on top of it).
 *
 * `computeFullParams` is set to true only when there is no preset and no key photo adjustments —
 * i.e., JPEG references with no editing parameters. In this case, all parameters are estimated
 * from scratch from the neural network output.
 *
 * @param keyAdjustments - Transfer from key photo (crop, healing, WB, exposure). WB presence
 *   determines `computeWb` (skip WB estimation if key already provided it).
 * @param presetAdjustments - Base preset for half color match. Undefined → full path.
 * @param keyPhotoColorAdjustments - Color match result of the key photo (cluster member re-use).
 * @param numRefs - How many reference images to use for multi-ref blending. Default from atom.
 */
async function getAdjustmentsFromColorMatch(options: {
	imageInfo: ImageInfoRecord
	referenceImages: Record<string, ImageInfoRecord>
	photo: PhotoRecord
	keyAdjustments: Partial<Adjustments>
	presetAdjustments?: Partial<Adjustments>
	keyPhotoColorAdjustments?: Partial<Adjustments>
	numRefs?: number // number of references to use for color match
}) {
	const {
		imageInfo,
		referenceImages,
		photo,
		keyAdjustments,
		presetAdjustments,
		keyPhotoColorAdjustments,
		numRefs = getNumRefsForColorMatch(),
	} = options

	let aiAdjustments: Partial<Adjustments> | undefined = undefined

	const keyIsRef = imageInfo.nearestReferences == null
	const nearestRef = keyIsRef
		? referenceImages[imageInfo.keyNeighborId ?? imageInfo.imageId]
		: getValidNearestRef(imageInfo.nearestReferences, referenceImages)
	let referenceFeatures: ReferenceColorMatchFeatures | null =
		nearestRef?.colorMatchReference ?? null

	if (referenceFeatures && !hasReferenceWebpData256x256Source(nearestRef)) {
		console.warn("referenceWebpData256x256 source is missing for", referenceFeatures.imageId)
		Sentry.captureMessage("referenceWebpData256x256 source is missing on nearest reference", {
			level: "warning",
			extra: {
				imageId: imageInfo.imageId,
				referenceImageId: referenceFeatures.imageId,
				nearestRef,
			},
		})
	}

	// For member photo that's very similar to the key photo, or has very similar neighbor processed, no need to compute color match
	if (adaptExposureOnlyFromKeyPhoto(imageInfo, keyAdjustments)) {
		logAiInfo(
			`[getAdjustmentsFromColorMatch] skip color match for ${photo.id}, use keyAdjustments`,
		)
		aiAdjustments = { ...keyPhotoColorAdjustments }
		aiAdjustments = addAutoCorrectionParamsIfNeeded(aiAdjustments, imageInfo)
		return aiAdjustments
	}

	if (!referenceFeatures || !nearestRef || !hasReferenceWebpData256x256Source(nearestRef)) {
		return aiAdjustments
	}

	let nearestRefWebpData: Uint8Array | null = null
	try {
		nearestRefWebpData = await getReferenceWebpData256x256(nearestRef)
	} catch (error) {
		console.warn(
			"Failed to hydrate referenceWebpData256x256 for",
			referenceFeatures.imageId,
			error,
		)
		Sentry.captureException(error)
		return aiAdjustments
	}

	if (!nearestRefWebpData?.length) {
		return aiAdjustments
	}

	logAiInfo(`[getAdjustmentsFromColorMatch] compute color match for ${photo.id}`)

	let allRefs: ImageInfoRecord[] = [nearestRef]
	if (numRefs > 1 && imageInfo.nearestReferences) {
		allRefs = imageInfo.nearestReferences
			.map((ref) => referenceImages[ref[0]])
			.filter((ref): ref is ImageInfoRecord => ref != null && ref != undefined)
		allRefs = allRefs.slice(0, numRefs)
	}
	const inputFeatures = getInputColorMatchFeatureWithFaceData(imageInfo)

	const isMemberPhoto = imageInfo.keyNeighborId && imageInfo.keyNeighborId !== imageInfo.imageId
	let baseAdjustments: Partial<Adjustments> | undefined = undefined
	if (presetAdjustments || isMemberPhoto) {
		let keyAdjmtWithoutStyleBiases = { ...keyAdjustments }
		if (isMemberPhoto && !keyIsRef) {
			keyAdjmtWithoutStyleBiases = applyReverseStyleWBBiases(
				keyAdjmtWithoutStyleBiases,
				imageInfo,
				referenceImages,
			)
		}

		// Cluster member photos: usually use preset adjustments as base for half color match; only if key is reference or no preset, use keyPhotoColorAdjustments (including key's light + sat) as base
		if (isMemberPhoto && !isEmpty(keyPhotoColorAdjustments)) {
			if (enableHighlightCorrection && !keyIsRef && presetAdjustments) {
				const keyPhotoAdvancedParams = getAdvancedParams(keyPhotoColorAdjustments ?? {}) // this is probably not needed as keyPhotoAdvancedParams is the same as preset adjustments in our current flow
				baseAdjustments = {
					...presetAdjustments,
					...keyPhotoAdvancedParams,
					...keyAdjmtWithoutStyleBiases,
				}
			} else {
				baseAdjustments = { ...keyPhotoColorAdjustments, ...keyAdjmtWithoutStyleBiases }
			}
		} else {
			// Key photo: use preset as base for half color match (compute light + sat + wb)
			baseAdjustments = { ...presetAdjustments }
			delete baseAdjustments.temperature
			delete baseAdjustments.tint
		}

		// add simple auto exposure for better color match
		baseAdjustments.exposure = getBaseExposure({
			imageInfo,
			nearestRef,
			inputFeatures,
			baseAdjustments,
		})

		// For key photo, add simple auto+bias white balance (good for photos with problematic wb)
		if (baseAdjustments.temperature == undefined) {
			const baseWB = getBaseWB({
				imageInfo,
				allRefs,
			})
			baseAdjustments.temperature = baseWB.temperature
			baseAdjustments.tint = baseWB.tint
		}

		delete baseAdjustments.localAdjustments
		DefaultExcludedAdjustmentKeys.forEach((key) => delete baseAdjustments?.[key])
	}

	const { colorStrength, featureDist } = getFeatureDistAndColorStrength({
		imageInfo,
		nearestRef,
	})

	// For key photo, if preset adjustments are derived from color match, interpolate the preset adjustments to reduce strength because the parameters may not be robust / versatile for different photos
	if (
		colorStrength < 1 &&
		baseAdjustments &&
		baseAdjustments.colorMatchImageId != null &&
		!isMemberPhoto
	) {
		const exposure = baseAdjustments.exposure ?? 0
		baseAdjustments = interpolateAdjustments(
			getDefaultAdjustments(photo),
			baseAdjustments,
			colorStrength,
		)
		baseAdjustments.exposure = exposure
		logAiInfo(
			"[getAdjustmentsFromColorMatch] interpolated base adjustments with color strength for colorMatchImageId != null in preset",
		)
	}

	logAiInfo("[getAdjustmentsFromColorMatch] color strength for nearest ref:", colorStrength)

	// reset color match strength and image id as we don't want ai style adjustments to show the ref's original color match image
	if (baseAdjustments) {
		baseAdjustments.colorMatchStrength = 1
		baseAdjustments.colorMatchImageId = null
	}

	// If no preset adjustments, use auto light params as base adjustments but compute full params
	let computeFullParams = false
	if (!baseAdjustments) {
		baseAdjustments =
			imageInfo.features.autoLightParams ??
			getAutoLightParams(
				imageInfo.features.light,
				imageInfo.features.colorMatch?.avgFaceHsvl ?? null,
			)
		// add auto wb
		const baseWB = getBaseWB({
			imageInfo,
			allRefs,
		})
		baseAdjustments.temperature = baseWB.temperature
		baseAdjustments.tint = baseWB.tint

		computeFullParams = true

		// For member photo, if no preset adjustments, get final adjustments from pure color match
		if (isMemberPhoto) {
			delete keyAdjustments.temperature
			delete keyAdjustments.tint
			delete keyAdjustments.saturation
		}
	}

	let result: {
		adjustmentsSkinToneOptimized: Adjustments | undefined
		computedInputFeatures: InputColorMatchFeaturesWithFaceData | null
		runtimeInfo: ColorMatchRuntimeInfo | undefined
	}

	// If key is ref, imageInfo.nearestReferences = null, just use the key neighbor ref for color match
	if (
		numRefs == 1 ||
		keyIsRef ||
		!imageInfo.nearestReferences ||
		imageInfo.nearestReferences.length == 1
	) {
		// If reference features do not have vegetationHsv, try to use the second nearest reference
		if (!referenceFeatures.vegetationHsv && numRefs == 1) {
			const secondRefId = imageInfo.nearestReferences?.[1]?.[0]
			if (secondRefId && secondRefId !== nearestRef?.imageId) {
				const secondRef = referenceImages[secondRefId]
				const secondRefVegetationHsv = secondRef?.colorMatchReference?.vegetationHsv
				if (secondRefVegetationHsv) {
					referenceFeatures = {
						...referenceFeatures,
						vegetationHsv: secondRefVegetationHsv,
					}
				}
			}
		}

		result = await getColorMatchParamsFromOneRef({
			imageInfo,
			photo,
			nearestRef,
			referenceFeatures,
			inputFeatures,
			baseAdjustments,
			colorStrength,
			featureDist,
			keyIsRef,
			computeFullParams,
			computeWb: keyAdjustments.temperature == undefined,
		})
	} else {
		result = await getColorMatchParamsFromMultipleRefs({
			imageInfo,
			photo,
			referenceImages,
			inputFeatures,
			baseAdjustments,
			keyIsRef,
			computeFullParams,
			numRefs,
			computeWb: keyAdjustments.temperature == undefined,
		})
	}

	if (result.computedInputFeatures) {
		saveInputColorMatchFeatureToImageInfo(imageInfo, result.computedInputFeatures)
	}

	aiAdjustments = result.adjustmentsSkinToneOptimized
	if (aiAdjustments) {
		aiAdjustments = addAutoCorrectionParamsIfNeeded(aiAdjustments, imageInfo)

		aiAdjustments = limitLightingRanges(aiAdjustments, nearestRef)
		if (result.runtimeInfo) {
			setColorMatchRuntimeInfo(photo.id, result.runtimeInfo)
		}
	} else {
		clearColorMatchRuntimeInfo(photo.id)
	}

	return aiAdjustments
}

/**
 * Calls the AI worker to run the neural color match pipeline for a single reference.
 * Handles reference bitmap caching (avoids re-transferring the 256×256 WebP on every call)
 * and has a retry path for cache-miss edge cases.
 *
 * @param imageInfo - ImageInfoRecord of the photo being processed (source of lum percentiles).
 * @param photo - PhotoRecord of the photo being processed.
 * @param nearestRef - ImageInfoRecord of the chosen reference; must have referenceWebpData256x256.
 * @param referenceFeatures - Pre-extracted color match features from the reference photo.
 * @param inputFeatures - Pre-computed neural input features for this photo, or null to let the
 *   worker compute them (result is returned as computedInputFeatures for reuse).
 * @param baseAdjustments - Base adjustments as the starting point, used both for neural network input rendering and parameter estimation.
 * @param colorStrength - Blend factor (0–1) based on feature distance to the reference.
 *   Reduces aggressiveness for distant references.
 * @param featureDist - Raw feature distance to the reference, passed through to the worker.
 * @param keyIsRef - True when the photo's key neighbor is itself the reference photo.
 * @param computeFullParams - True to compute the full adjustment set; false for half color match.
 * @param computeWb - False when key photo already provided WB (avoids double-computing and inconsistency).
 */
async function getColorMatchParamsFromOneRef(options: {
	imageInfo: ImageInfoRecord
	photo: PhotoRecord
	nearestRef: ImageInfoRecord
	referenceFeatures: ReferenceColorMatchFeatures
	inputFeatures: InputColorMatchFeaturesWithFaceData | null
	baseAdjustments: Partial<Adjustments>
	colorStrength: number
	featureDist: number
	keyIsRef: boolean
	computeFullParams: boolean
	computeWb: boolean
}) {
	const { imageInfo, nearestRef, referenceFeatures } = options

	const referenceId = getReferenceCacheId({
		referenceImageId: referenceFeatures.imageId,
		adjustmentsDigest: referenceFeatures.adjustmentsDigest,
	})
	const referenceCached = colorMatchCache.hasReferenceBitmapInAIWorker(referenceId)

	const referenceWebpData = referenceCached
		? nearestRef.referenceWebpData256x256
		: await getReferenceWebpData256x256(nearestRef)

	// make copy for comlink transfer as it will be cleared after transfer
	let webpDataCopy: Uint8Array | undefined = undefined
	if (!referenceCached) {
		if (!referenceWebpData?.length) {
			throw new Error("referenceWebpData256x256 is missing for " + referenceFeatures.imageId)
		}
		webpDataCopy = copyUint8Array(referenceWebpData)
	}

	const params = {
		...options,
		referenceId,
		referenceWebpData: webpDataCopy,
		lumPercentiles: imageInfo.features.light.percentiles,
	}
	let result: {
		adjustmentsSkinToneOptimized: Adjustments
		computedInputFeatures: InputColorMatchFeaturesWithFaceData | null
		runtimeInfo: ColorMatchRuntimeInfo
	}
	// Just a safety guard: retry with webpData if cache miss (supposedly cache sync issue)
	try {
		result = await AI.getNeuralColorMatchParams(
			transfer(params, webpDataCopy ? [webpDataCopy.buffer] : []),
		)
	} catch (error) {
		if (
			!webpDataCopy &&
			error instanceof Error &&
			error.message.includes(AI_WORKER_REF_CACHE_MISS_ERROR)
		) {
			console.warn(
				`Cache sync issue detected for reference ${referenceId}, retrying with webpData...`,
			)
			const retryReferenceWebpData = await getReferenceWebpData256x256(nearestRef)
			if (!retryReferenceWebpData?.length) {
				throw new Error(
					"referenceWebpData256x256 is missing for " + referenceFeatures.imageId,
				)
			}
			webpDataCopy = copyUint8Array(retryReferenceWebpData)
			result = await AI.getNeuralColorMatchParams(
				transfer({ ...params, referenceWebpData: webpDataCopy }, [webpDataCopy.buffer]),
			)
		} else {
			throw error
		}
	}

	if (!referenceCached) {
		colorMatchCache.setAiWorkerReferenceBitmap(referenceId)
	}

	return result
}

async function buildColorMatchReferencesForMultipleRefs(options: {
	imageInfo: ImageInfoRecord
	referenceImages: Record<string, ImageInfoRecord>
	numRefs: number
	includeWebpDataForAll?: boolean
}) {
	const { imageInfo, referenceImages, numRefs, includeWebpDataForAll = false } = options

	const references: ReferenceItem[] = []
	const cachedReferenceIds: string[] = []
	const nearestReferences = imageInfo.nearestReferences ?? []

	for (const ref of nearestReferences) {
		const refId = ref[0]
		const refInfo = referenceImages[refId]
		if (refInfo?.colorMatchReference && hasReferenceWebpData256x256Source(refInfo)) {
			const { colorStrength, featureDist } = getFeatureDistAndColorStrength({
				imageInfo,
				nearestRef: refInfo,
			})
			const referenceFeatures = refInfo.colorMatchReference
			const referenceId = getReferenceCacheId({
				referenceImageId: referenceFeatures.imageId,
				adjustmentsDigest: referenceFeatures.adjustmentsDigest,
			})
			const referenceCached = colorMatchCache.hasReferenceBitmapInAIWorker(referenceId)
			if (referenceCached) {
				cachedReferenceIds.push(referenceId)
			}

			let referenceWebpData = refInfo.referenceWebpData256x256
			if (includeWebpDataForAll || !referenceCached) {
				try {
					referenceWebpData = await getReferenceWebpData256x256(refInfo)
				} catch (error) {
					console.warn(
						"Failed to hydrate referenceWebpData256x256 for multi-ref color match",
						{ refId, error },
					)
					Sentry.captureException(error)
					continue
				}
			}

			if ((includeWebpDataForAll || !referenceCached) && !referenceWebpData?.length) {
				continue
			}

			// Make a copy for comlink transfer as it will be detached after transfer.
			let webpDataCopy: Uint8Array | undefined = undefined
			if (includeWebpDataForAll || !referenceCached) {
				if (!referenceWebpData?.length) {
					continue
				}
				webpDataCopy = copyUint8Array(referenceWebpData)
			}

			const imageWbDist = imageWbDistance(imageInfo.features, refInfo.features)
			references.push({
				referenceId,
				referenceFeatures,
				featureDist,
				imageWbDist,
				colorStrength,
				webpData256x256: webpDataCopy,
			})
		}
		if (references.length >= numRefs) {
			break
		}
	}

	return { references, cachedReferenceIds }
}

/**
 * Calls the AI worker to run multi-reference neural color match (K>1 path).
 * Collects up to `numRefs` nearest references from `imageInfo.nearestReferences`,
 * computes per-reference colorStrength and featureDist, then delegates to
 * `AI.getNeuralColorMatchParamsMultiRef` which blends K LUT results before
 * running parameter estimation once on the blended output.
 *
 * Falls back gracefully if fewer than `numRefs` valid references are found.
 * If a reference lacks `vegetationHsv`, supplements from the second-nearest ref.
 *
 * @param imageInfo - ImageInfoRecord of the photo being processed; nearestReferences drives
 *   reference selection order.
 * @param photo - PhotoRecord of the photo being processed.
 * @param referenceImages - All available reference ImageInfoRecords keyed by photo ID.
 * @param inputFeatures - Pre-computed neural input features for this photo, or null to let the
 *   worker compute them (result is returned as computedInputFeatures for reuse).
 * @param baseAdjustments - Base adjustments as the starting point for half color match, used for rendering neural network input and parameter estimation.
 * @param keyIsRef - True when the photo's key neighbor is itself a reference photo.
 * @param computeFullParams - True to compute the full adjustment set; false for half color match.
 * @param numRefs - Maximum references to blend (default from numRefsForColorMatchAtom, typically 3).
 * @param computeWb - False when key photo already provided WB (avoids double-computing and inconsistency).
 */
async function getColorMatchParamsFromMultipleRefs(options: {
	imageInfo: ImageInfoRecord
	photo: PhotoRecord
	referenceImages: Record<string, ImageInfoRecord>
	inputFeatures: InputColorMatchFeaturesWithFaceData | null
	baseAdjustments: Partial<Adjustments>
	keyIsRef: boolean
	computeFullParams: boolean
	numRefs: number
	computeWb: boolean
}) {
	const { imageInfo, referenceImages, numRefs } = options

	const { cachedReferenceIds, references: initialReferences } =
		await buildColorMatchReferencesForMultipleRefs({
			imageInfo,
			referenceImages,
			numRefs,
		})
	let references = initialReferences

	if (references.length == 0) {
		console.error(
			"No references found for color match in getColorMatchParamsFromMultipleRefs",
			{ imageInfo, references },
		)
		return {
			adjustmentsSkinToneOptimized: undefined,
			computedInputFeatures: null,
			runtimeInfo: undefined,
		}
	}

	const params = {
		...options,
		lumPercentiles: imageInfo.features.light.percentiles,
	}

	const runMultiRefColorMatch = async (refs: ReferenceItem[]) => {
		const transferWebpData = refs
			.map((ref) => (ref.webpData256x256 ? ref.webpData256x256.buffer : null))
			.filter((buffer): buffer is ArrayBuffer => buffer != null)

		return await AI.getNeuralColorMatchParamsMultiRef(
			transfer({ ...params, references: refs }, transferWebpData),
		)
	}

	let result: Awaited<ReturnType<typeof AI.getNeuralColorMatchParamsMultiRef>>
	try {
		result = await runMultiRefColorMatch(references)
	} catch (error) {
		if (error instanceof Error && error.message.includes(AI_WORKER_REF_CACHE_MISS_ERROR)) {
			console.warn("Cache sync issue detected for multi-ref color match, retrying...", {
				referenceIds: references.map((ref) => ref.referenceId),
				cachedReferenceIds,
			})

			for (const referenceId of cachedReferenceIds) {
				colorMatchCache.removeAiWorkerReferenceBitmap(referenceId)
			}

			const retryReferences = (
				await buildColorMatchReferencesForMultipleRefs({
					imageInfo,
					referenceImages,
					numRefs,
					includeWebpDataForAll: true,
				})
			).references
			references = retryReferences

			result = await runMultiRefColorMatch(references)
		} else {
			throw error
		}
	}

	for (const ref of references) {
		if (ref.webpData256x256) {
			colorMatchCache.setAiWorkerReferenceBitmap(ref.referenceId)
		}
	}

	return result
}

function shouldApplyPreset(photo: PhotoRecord, preset: PresetItem, strength: number) {
	const currentAppliedPreset = photo.presetId

	if (photo.isUnsupported) {
		return false
	}
	if (photo.isHidden) {
		return false
	}
	// Only apply preset if it is not currently applied
	return currentAppliedPreset !== getPresetIdWithStrength(preset.id, strength)
}

const getPresetAdjustments = cached(
	async (photo: PhotoRecord, preset: PresetItem) => {
		const file = getFileForPhoto(photo)

		if (!file) {
			throw new Error("Photo record not found")
		}

		const currentImageInfo = await getOrExtractImageInfo(photo)

		const metadata = getPhotoMetadata(photo.id)

		await AI.loadImage(file, { metadata })

		const aiAdjustmentsMap = currentImageInfo.aiAdjustmentsMap
		const presetAdjustmentsMap = currentImageInfo.presetAdjustmentsMap ?? {}

		const currentAdjustments = await CatalogManager.fetchAdjustments(photo)
		const photoInfo = { photo, currentAdjustments, currentImageInfo }

		const showLoadingTimer = setTimeout(() => {
			store.set(loadingPresetsAtom, { ...store.get(loadingPresetsAtom), [preset.id]: true })
		}, 200)
		const presetAdjustments = await loadPresetAdjustments(preset)
		clearTimeout(showLoadingTimer)
		store.set(loadingPresetsAtom, { ...store.get(loadingPresetsAtom), [preset.id]: false })

		const style = store.get(currentStyleAtom)
		const defaultAdjustments = { ...getDefaultAdjustments(photo), ...defaultWhiteBalance }

		if (style?.presets[preset.id] == undefined)
			presetAdjustmentsMap[preset.id] = presetAdjustments

		const finalAdjustmentsSet = [0, 1, 2].map(async (strength) => {
			const presetIdWithStrength = getPresetIdWithStrength(preset.id, strength)
			let interpolatedAdjustments: Partial<Adjustments> = interpolateAdjustments(
				defaultAdjustments,
				presetAdjustments,
				strength,
			)
			interpolatedAdjustments = applyPresetStrengthToUserLutAdjustments(
				interpolatedAdjustments,
				presetAdjustments,
				strength,
			)

			// Transform mask for paste
			const transformedPresetAdjustments = transformPresetMaskForPaste(
				currentImageInfo,
				interpolatedAdjustments,
				currentAdjustments,
			)
			// merged preset adjustments without auto
			const mergedAdjustments = { ...currentAdjustments, ...transformedPresetAdjustments }

			if (shouldApplyAutoOnPreset()) {
				const presetInfo = {
					presetId: presetIdWithStrength,
					transformedPresetAdjustments,
					presetAdjustmentsMap,
					style: style,
					strength: strength,
				}

				// Aggregate presets
				const { aggregatedPresetId, isOverwriting, baseAdjustments } =
					await aggregatePresetsAndCheckOverwriting(photoInfo, presetInfo)

				// Get auto adjustments
				let aiAdjustments = isOverwriting
					? photoInfo.currentImageInfo.aiAdjustmentsMap[aggregatedPresetId]
					: undefined

				// When preset strength = 0, and is overwriting, aiAdjustments is equal to original auto
				let useNullPresetID = false
				if (
					strength === 0 &&
					isOverwriting &&
					aggregatedPresetId === presetIdWithStrength
				) {
					useNullPresetID = true
					aiAdjustments = photoInfo.currentImageInfo.aiAdjustmentsMap[nullPresetID]
				}

				if (!aiAdjustments) {
					aiAdjustments = await AI.calculateAutoParams(
						baseAdjustments,
						photoInfo.currentImageInfo,
					)

					// Add auto perspective or straighten params:
					aiAdjustments = addAutoCorrectionParamsIfNeeded(aiAdjustments, currentImageInfo)

					if (isOverwriting) {
						const idToStore = useNullPresetID ? nullPresetID : aggregatedPresetId
						aiAdjustmentsMap[idToStore] = aiAdjustments
					}
				}

				let finalAdjustments = { ...baseAdjustments, ...aiAdjustments }
				finalAdjustments = combineAdjustmentsAndMaintainNonColorParams(
					currentAdjustments,
					finalAdjustments,
				)
				logAiInfo({ baseAdjustments, aiAdjustments, finalAdjustments })

				const result: PresetAdjustmentsResult = {
					presetId: preset.id,
					strength,
					presetAdjustments: mergedAdjustments,
					finalAdjustments,
				}

				return result
			} else {
				const result: PresetAdjustmentsResult = {
					presetId: preset.id,
					strength,
					presetAdjustments: mergedAdjustments,
					finalAdjustments: null,
				}
				return result
			}
		})

		const imageInfo = {
			...currentImageInfo,
			aiAdjustmentsMap,
			presetAdjustmentsMap,
		}

		updateImageInfo(imageInfo)

		const min = await finalAdjustmentsSet[0]
		const mid = await finalAdjustmentsSet[1]
		const max = await finalAdjustmentsSet[2]

		if (min && mid && max) {
			const result: PresetAdjustmentsInfo = {
				min,
				mid,
				max,
				photoId: photo.id,
				currentAdjustmentsDigest: photo.adjustmentsDigest,
			}
			return { result, presetAdjustments }
		} else {
			throw new Error("Calculated adjustments error")
		}
	},
	(photo, preset) => photo.id + preset.id + shouldApplyAutoOnPreset() + photo.adjustmentsDigest,
)

async function applyPreset(
	photo: PhotoRecord,
	adjustmentsResult: PresetAdjustmentsResult,
	applyAuto: boolean,
): Promise<ApplyPresetResult> {
	const file = getFileForPhoto(photo)
	const metadata = getPhotoMetadata(photo.id)

	if (!file) {
		throw new Error("Photo record not found")
	}

	await AI.loadImage(file, { metadata })

	let finalPreview = await AI.generatePreview({
		adjustments: adjustmentsResult.presetAdjustments,
	})

	// finalAdjustments is null when the preset info was computed with auto off;
	// storing it as a history step corrupts the photo's edit history (PHOTO-EDITOR-109)
	const autoAdjustments = applyAuto ? adjustmentsResult.finalAdjustments : null
	if (applyAuto && !autoAdjustments) {
		Sentry.captureMessage("Preset auto adjustments missing, skipping auto step", {
			level: "warning",
			extra: { presetId: adjustmentsResult.presetId, strength: adjustmentsResult.strength },
		})
	}
	if (autoAdjustments) {
		finalPreview = await AI.generatePreview({ adjustments: autoAdjustments })
	}

	const presetResults = {
		adjustments: adjustmentsResult.presetAdjustments,
		imageInfo: null,
		preview: null,
		originalPreview: null,
	}
	const finalResults = autoAdjustments
		? {
				adjustments: autoAdjustments,
				imageInfo: null,
				preview: finalPreview,
				originalPreview: null,
			}
		: null

	return { finalResults, presetResults }
}

async function getOrComputeAutoAdjustments(photo: PhotoRecord) {
	const imageInfo = getImageInfo(photo.id)
	const aiAdjustments = imageInfo?.aiAdjustmentsMap[nullPresetID]
	if (aiAdjustments) {
		return { imageInfo, aiAdjustments, isPhotoLoaded: false }
	} else {
		const result = await loadPhotoAndComputeAutoAdjustments(photo)

		return {
			...result,
			isPhotoLoaded: true,
		}
	}
}

async function loadPhotoAndComputeAutoAdjustments(photo: PhotoRecord) {
	const file = getFileForPhoto(photo)

	if (!file) {
		throw new Error("Photo record not found")
	}

	const currentImageInfo = await getOrExtractImageInfo(photo)
	const metadata = getPhotoMetadata(photo.id)

	await AI.loadImage(file, { metadata })

	const aiAdjustmentsMap = currentImageInfo.aiAdjustmentsMap

	let aiAdjustments = aiAdjustmentsMap[nullPresetID]

	if (!aiAdjustments) {
		logAiInfo("compute auto style for", photo.id)
		aiAdjustments = await AI.getDefaultAutoParamsFast(photo, currentImageInfo)

		// Add auto perspective or straighten params:
		aiAdjustments = addAutoCorrectionParamsIfNeeded(aiAdjustments, currentImageInfo)

		aiAdjustmentsMap[nullPresetID] = aiAdjustments
	}

	const imageInfo = {
		...currentImageInfo,
		aiAdjustmentsMap: aiAdjustmentsMap,
	}

	return { imageInfo, aiAdjustments }
}

async function applyBasicAutoAdjustments(photo: PhotoRecord): Promise<AdjustmentsResult> {
	const currentAdjustments = await CatalogManager.fetchAdjustments(photo)
	const { imageInfo, aiAdjustments } = await getOrComputeAutoAdjustments(photo)

	const basicsAIAdjustments = { ...aiAdjustments }
	//Deleting straightening and perspectiveVertical
	delete basicsAIAdjustments.crop
	delete basicsAIAdjustments.cropStraighten
	delete basicsAIAdjustments.perspectiveVertical

	const adjustments = { ...currentAdjustments, ...basicsAIAdjustments }

	return { adjustments, imageInfo, preview: null, originalPreview: null }
}

async function applyAutoAdjustments(photo: PhotoRecord): Promise<AdjustmentsResult> {
	const { imageInfo, aiAdjustments, isPhotoLoaded } = await getOrComputeAutoAdjustments(photo)

	const defaultAdjustments = getDefaultAdjustments(photo)
	const adjustments = { ...defaultAdjustments, ...aiAdjustments }

	const preview = isPhotoLoaded === true ? await AI.generatePreview({ adjustments }) : null

	return {
		adjustments,
		imageInfo,
		preview,
		originalPreview: null,
	}
}

async function pasteAdjustments(
	photo: PhotoRecord,
	adjustments: Partial<Adjustments>,
): Promise<AdjustmentsResult> {
	const file = getFileForPhoto(photo)

	if (!file) {
		throw new Error("Cannot paste adjustments. File not found")
	}

	const currentAdjustments = await CatalogManager.fetchAdjustments(photo)
	const finalAdjustments = { ...currentAdjustments, ...adjustments }

	// transform mask for paste correctly
	if (adjustments["localAdjustments"]) {
		const imageInfo = await getOrExtractImageInfo(photo)

		finalAdjustments["localAdjustments"] = transformLocalAdjustmentsForPaste(
			imageInfo,
			adjustments["localAdjustments"] ?? [],
			finalAdjustments,
		)
	}

	const metadata = getPhotoMetadata(photo.id)
	await AI.loadImage(file, { metadata })
	const preview = await AI.generatePreview({ adjustments: finalAdjustments })

	return {
		adjustments: finalAdjustments,
		imageInfo: null,
		preview,
		originalPreview: null,
	}
}

export function getScaledDeltas(deltas: Record<string, number>): Partial<Adjustments> {
	const scaleFactorMap = {
		exposure: 0.1,
		contrast: 0.1,
		saturation: 0.05,
		temperature: 0.05,
		tint: 5,
	}
	const defaultScaleFactor = 0.2

	const scaledDeltas = { ...deltas }
	for (const key in deltas) {
		scaledDeltas[key] = (deltas[key] ?? 0) * (scaleFactorMap[key] ?? defaultScaleFactor)
	}
	return scaledDeltas
}

/**
 * Add two style biases together by summing each numeric field
 * @param base - Base style biases (can be undefined/null)
 * @param additions - Additional biases to add
 * @returns Combined style biases
 */
export function addStyleBiases(
	base: Partial<Adjustments> | undefined | null,
	additions: Partial<Adjustments>,
): Partial<Adjustments> {
	if (!base) {
		return { ...additions }
	}

	const result: Partial<Adjustments> = { ...base }

	for (const key in additions) {
		const typedKey = key as keyof Adjustments
		const baseValue = base[typedKey]
		const addValue = additions[typedKey]

		// Only add numeric values
		if (typeof addValue === "number") {
			if (typeof baseValue === "number") {
				// @ts-expect-error - We know this is a number field
				result[typedKey] = baseValue + addValue
			} else {
				// @ts-expect-error - We know this is a number field
				result[typedKey] = addValue
			}
		}
	}

	return result
}

export async function applyAdjustmentDeltas(
	photo: PhotoRecord,
	currentAdjustments: Adjustments,
	deltas: Record<string, number>,
) {
	const file = getFileForPhoto(photo)

	if (!file) {
		throw new Error("Cannot apply adjustments. File not found")
	}

	const styleBiases = getScaledDeltas(deltas)
	const imageInfo = getImageInfo(photo.id)
	const asShotTemperature = imageInfo?.features?.meta?.init_temp
	let adjustments = { ...currentAdjustments }
	adjustments = applyStyleBiases({ adjustments, styleBiases, asShotTemperature }) as Adjustments

	const metadata = getPhotoMetadata(photo.id)
	await AI.loadImage(file, { metadata })
	const preview = await AI.generatePreview({ adjustments })

	return {
		adjustments,
		preview,
		id: photo.id,
	}
}
